import React, { useState, useEffect } from "react";
import { Row, Col, Typography, Card, Button, Statistic, Progress, Alert, Modal, Divider } from "antd";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { QuestionCircleOutlined, WarningOutlined } from "@ant-design/icons";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import DayEndAlert from './components//DayEndAlert'; // Import the new component

const DayEndDashboard = () => {
  const [isLoading, setIsLoading] = useState(false); // Loading for day-end process
  const [apiLoading, setApiLoading] = useState(true); // Loading for API fetch
  const { Title, Text } = Typography;
  const { confirm } = Modal;
  const apiUrl = process.env.REACT_APP_API_URL;
  const currency = localStorage.getItem("currencycode") || "₹";
  const business_date = localStorage.getItem("business_date");
  
  const [selectedDate, setSelectedDate] = useState(moment(business_date));
  const [dashboardData, setDashboardData] = useState(null);
  const navigate = useNavigate();



  const handleNavigation = (type) => {
    switch (type) {
      case 'arrivals':
        navigate('/hospitality/guest-list-card');
        break;
      case 'departure':
        navigate('/hospitality/list-depature-guest-card');
        break;
      case 'invoice':
        navigate('/hospitality/day-invoices');
        break;
      default:
        console.warn('Invalid navigation type');
    }
  };


  useEffect(() => {
    const fetchDashboardData = async () => {
      setApiLoading(true);
      const token = localStorage.getItem("token");

      try {
        const response = await axios.get(
          `${apiUrl}/nightaudit/night-audit-summary-due`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setDashboardData(response.data.summary);
        } else {
          throw new Error("Failed to fetch dashboard data");
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error("Failed to load dashboard data. Please try again.");
        setDashboardData({
          pending_checkouts: 0,
          due_arrivals: 0,
          unsettled_bills: 0,
          financial_summary: {
            total_revenue: 0,
            room_revenue: 0,
            fnb_revenue: 0,
            expenses: 0,
            cash_collection: 0,
            upi_collection: 0,
            bank_transfer_card_collection: 0,
            net_cash_position: 0,
          },
        });
      } finally {
        setApiLoading(false);
      }
    };

    fetchDashboardData();
  }, [apiUrl, business_date]);

  const handleRunDayEnd = () => {
    const hasPendingItems = 
      (dashboardData?.pending_checkouts || 0) > 0 ||
      (dashboardData?.due_arrivals || 0) > 0 ||
      (dashboardData?.unsettled_bills || 0) > 0;
  
    if (!hasPendingItems) {
      // No pending items, directly run the day-end process
      handleDayEndProcess();
      return;
    }
  
    // Show confirmation prompt if there are pending items
    confirm({
      title: 'Confirm Day-End Process',
      icon: <QuestionCircleOutlined />,
      content: (
        <div>
          <Text strong>Pending items to review:</Text>
          <ul>
            {dashboardData?.pending_checkouts > 0 && 
              <li>{dashboardData.pending_checkouts} pending checkouts</li>}
            {dashboardData?.due_arrivals > 0 && 
              <li>{dashboardData.due_arrivals} due arrivals</li>}
            {dashboardData?.unsettled_bills > 0 && 
              <li>{dashboardData.unsettled_bills} unsettled bills</li>}
          </ul>
          <Text type="secondary">Are you sure you want to proceed?</Text>
        </div>
      ),
      onOk() {
        handleDayEndProcess();
      },
      onCancel() {},
    });
  };

  const handleDayEndProcess = async () => {
    const token = localStorage.getItem("token");

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to process the Day-End now?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await axios.post(
            `${apiUrl}/nightaudit/run-night-audit`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );

          if (response.status === 200) {
            const newDate = new Date(response.data.newDate);
            const formattedDate = newDate.toLocaleDateString("en-US", { 
              weekday: "long", 
              year: "numeric", 
              month: "long", 
              day: "numeric" 
            });

            Swal.fire({
              title: "Day-End Successfully Processed!",
              text: `New Date is ${formattedDate}. Have a nice day!`,
              icon: "success",
              confirmButtonText: "OK",
            }).then(() => {
              //setDashboardData(null);
              localStorage.setItem("business_date", newDate);
              localStorage.removeItem("selectedRooms");
              localStorage.removeItem("billingSummary");
              localStorage.removeItem("searchState");
              localStorage.removeItem("searchParams");
              localStorage.removeItem("filteredRooms");
              window.location.href = '/hospitality';
            });
          } else {
            throw new Error("Unexpected response from server.");
          }
        } catch (error) {
          setIsLoading(false);
          if (error.response && error.response.status === 400) {
            const errorMessage = error.response.data.message || "An error occurred.";
            const actionRequired = error.response.data.actionRequired || "";
            Swal.fire({
              title: "Night Audit Blocked!",
              html: `<strong>${errorMessage}</strong><br><br><em>${actionRequired}</em>`,
              icon: "warning",
              confirmButtonText: "OK",
            });
          } else {
            Swal.fire({
              title: "Error!",
              text: "Failed to process Day-End. Please try again later.",
              icon: "error",
              confirmButtonText: "OK",
            });
          }
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const totalRevenue = dashboardData?.financial_summary?.total_revenue || 0;
  const netCashPosition = dashboardData?.financial_summary?.net_cash_position || 0;

  const readinessItems = [
    dashboardData?.pending_checkouts || 0,
    dashboardData?.due_arrivals || 0,
    dashboardData?.unsettled_bills || 0,
  ];
  const totalItems = readinessItems.length;
  const issueCount = readinessItems.filter(item => item > 0).length;
  const readinessPercent = Math.round((1 - (issueCount / totalItems) * 0.33) * 100);

  // Combine both loading states
  const loading = apiLoading || isLoading;

  // Add CSS for the loader (if not already in a global stylesheet)
  const loaderStyles = `
    #loader-wrapper- {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 200px;
    }
    .loader {
      display: flex;
      gap: 8px;
    }
    .dot {
      width: 10px;
      height: 10px;
      background-color: #1890ff;
      border-radius: 50%;
      animation: bounce 0.5s infinite alternate;
    }
    .dot:nth-child(2) { animation-delay: 0.1s; }
    .dot:nth-child(3) { animation-delay: 0.2s; }
    .dot:nth-child(4) { animation-delay: 0.3s; }
    .dot:nth-child(5) { animation-delay: 0.4s; }
    @keyframes bounce {
      to { transform: translateY(-10px); }
    }
  `;

  return (
    <div className="page-wrapper">
      <style>{loaderStyles}</style>
      {loading ? (
        <div id="loader-wrapper-">
          <div className="loader">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      ) : (
        <div className="content container-fluid">
          {/* Custom Styles for Vertical Line */}
          <style>
            {`
              .vertical-line {
                width: 2px;
                height: 50px;
                background-color: #ccc;
                margin: auto;
              }
              .financial-stat {
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
              }
              .financial-stat i {
                font-size: 22px;
                margin-bottom: 5px;
              }
            `}
          </style>

          <Row gutter={16}>
            <Col span={24}>
              <Card 
                title={
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <h3 style={{ fontWeight: "bold", color: "#002147", marginBottom: 0 }}>Day-End Readiness Checklist</h3>
                   
                    <DayEndAlert />
                  </div>
                }
                extra={
                  <Button
                    type="primary"
                    size="large"
                    style={{
                      fontWeight: "bold",
                      padding: "8px 16px",
                      fontSize: "14px",
                      borderRadius: "6px"
                    }}
                    icon={<i className="fas fa-play-circle"></i>}
                    onClick={handleRunDayEnd}
                    disabled={isLoading}
                  >
                    Run Day-End Process
                  </Button>
                }
              >
                <Row gutter={24} align="middle" justify="center" className="text-center">
                  <Col span={5}>
                    <Statistic
                      title={
                        <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                          <i className="fas fa-door-open text-primary me-2" style={{ fontSize: "22px" }}></i>
                          Pending Checkouts
                          <Text type="secondary" style={{ display: 'block', fontSize: "14px", fontWeight: "normal", color: "#666" }}>
                            Rooms needing checkout processing
                          </Text>
                        </span>
                      }
                      value={dashboardData.pending_checkouts}
                      suffix="rooms"
                    />
                  <span className="view-details" onClick={() => handleNavigation('departure')}>View Details</span>

                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title={
                        <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                          <i className="fas fa-user-clock text-warning me-2" style={{ fontSize: "22px" }}></i>
                          Due Arrivals
                          <Text type="secondary" style={{ display: 'block', fontSize: "14px", fontWeight: "normal", color: "#666" }}>
                            Guests expected today
                          </Text>
                        </span>
                      }
                      value={dashboardData.due_arrivals}
                    />
                  <span className="view-details" onClick={() => handleNavigation('arrivals')}>View Details</span>
 
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={5}>
                    <Statistic
                      title={
                        <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                          <i className="fas fa-money-bill-wave text-danger me-2" style={{ fontSize: "22px" }}></i>
                          Unsettled Bills
                          <Text type="secondary" style={{ display: 'block', fontSize: "14px", fontWeight: "normal", color: "#666" }}>
                            Unpaid guest balances
                          </Text>
                        </span>
                      }
                      value={dashboardData.unsettled_bills}
                    />
                    <span className="view-details" onClick={() => handleNavigation('invoice')}>View Details</span>


                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={5}>
                    <Progress
                      type="dashboard"
                      percent={readinessPercent}
                      strokeColor={readinessPercent === 100 ? "#52c41a" : "#1890ff"}
                      format={percent => (
                        <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                          {`${percent}% Ready`}
                        </span>
                      )}
                    />
                    
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col span={24}>
              <Card title={<h3 style={{ fontWeight: "bold", color: "#002147" }}>Daily Financial Summary</h3>}>
                <Row gutter={24} align="middle" justify="center" className="text-center">
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-chart-line text-success"></i>
                    <Statistic
                      title={<Text strong>Total Revenue</Text>}
                      value={parseFloat(dashboardData.financial_summary.total_revenue) 	 || 0}
                      prefix={currency}
                      valueStyle={{ fontSize: 28, color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-hotel text-primary"></i>
                    <Statistic
                      title={<Text strong>Room Revenue</Text>}
                      value={parseFloat(dashboardData.financial_summary.room_revenue) || 0}
                      prefix={currency}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-utensils text-warning"></i>
                    <Statistic
                      title={<Text strong>F&B Revenue</Text>}
                      value={parseFloat(dashboardData.financial_summary.room_service_revenue) || 0}
                      prefix={currency}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-receipt text-secondary"></i>
                    <Statistic
                      title={<Text strong>Expenses</Text>}
                      value={parseFloat(dashboardData.financial_summary.expenses) || 0}
                      prefix={currency}
                    />
                  </Col>
                </Row>

                <div style={{ margin: "20px 0" }}>
                  <hr style={{ border: "1px dashed #ccc" }} />
                </div>

                <Row gutter={24} align="middle" justify="center" className="text-center">
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-wallet text-success"></i>
                    <Statistic
                      title={<Text strong>Cash Collection</Text>}
                      value={parseFloat(dashboardData.financial_summary.cash_collection) || 0}
                      prefix={currency}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-mobile-alt text-info"></i>
                    <Statistic
                      title={<Text strong>UPI Collection</Text>}
                      value={parseFloat(dashboardData.financial_summary.upi_collection) || 0}
                      prefix={currency}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-credit-card text-danger"></i>
                    <Statistic
                      title={<Text strong>Bank Transfer / Credit Card</Text>}
                      value={parseFloat(dashboardData.financial_summary.bank_transfer_collection	) || 0}
                      prefix={currency}
                    />
                  </Col>
                  <Col span={1}>
                    <div className="vertical-line"></div>
                  </Col>
                  <Col span={4} className="financial-stat">
                    <i className="fas fa-balance-scale text-dark"></i>
                    <Statistic
                      title={<Text strong>Net Cash Position</Text>}
                      value={netCashPosition}
                      prefix={currency}
                      valueStyle={{ 
                        color: netCashPosition >= 0 ? '#3f8600' : '#cf1322',
                        fontSize: 24
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default DayEndDashboard;